import { css, cx } from "@linaria/core"
import { styled } from "@linaria/react"
import { observer } from "mobx-react-lite"


import { Button } from "@/components/Button/Button.tsx"
import { AgentMessageContainer } from "@/components/Chat/AgentMessageStyles.ts"
import { ServiceListMessageStyles } from "@/components/Chat/NewProject/ServiceListMessageStyles.ts"
import { ServiceListModel, type ServiceListModelParams } from "@/components/Chat/NewProject/ServiceListViewModel.ts"
import { Horizontal, Vertical } from "@/components/Flex.ts"
import { PromptInputStyles } from "@/components/PromptInput/PromptInputStyles.ts"
import { ResizeableTextarea } from "@/components/ResizeableTextarea/ResizeableTextarea.tsx"
import { Toggle } from "@/components/Toggle/Toggle.tsx"
import { Tooltip } from "@/components/Tooltip/Tooltip.tsx"
import { useModel } from "@/hooks/useModel.ts"
import { useOrgContext } from "@/hooks/useRootContext.ts"
import { ChevronRightLight, Icon12 } from "@/icons/Icons.ts"
import { Rotate0, Rotate90 } from "@/styles/mixins.ts"
import * as textStyles from "@/styles/textStyles.ts"

import type { ServiceItemModel } from "@/components/Chat/NewProject/ServiceItemModel.ts"
import type { AiChatMessageContent } from "@/lib/client/generated/TypeDefinitions.ts"
import type { IObservableValue } from "mobx"

interface ServiceItemProps {
  model: ServiceItemModel
}

const ServiceItem = observer(({ model }: ServiceItemProps) => {
  const isDescriptionExpanded = model.expanded.get()
  const disabled = model.disabled.get()
  const isEditing = model.isEditing.get()

  return (
    <Vertical className={cx(
      disabled ? Disabled : null,
      ServiceListMessageStyles.Container,
      disabled ? ServiceListMessageStyles.Container_Disabled : ServiceListMessageStyles.Container_Active
    )}>
      <Horizontal justifyContent="space-between" alignItems="center">
        <ServiceName className={Disableable}>{model.service.name}</ServiceName>
        {isEditing && (
          <Tooltip content={disabled ? "Enable service" : "Disable service"}>
            <Toggle active={!disabled} onClick={() => model.setDisabled(!disabled)} />
          </Tooltip>
        )}
      </Horizontal>

      <Vertical gap="8px" className={Disableable}>
        <ServiceDetails>
          {model.service.description}
        </ServiceDetails>

        <CommentInputWrapper>
          <CommentLabel onClick={() => model.setExpanded(!isDescriptionExpanded)} disabled={disabled}>
            <span>
              Notes on execution
            </span>
            {isDescriptionExpanded
              ? <ChevronRightLight className={cx(Rotate90, Icon12)} color="var(--main-color)" />
              : <ChevronRightLight className={cx(Rotate0, Icon12)} color="var(--main-color)" />}
          </CommentLabel>

          {isDescriptionExpanded && !disabled && (
            <InputContainer className={PromptInputStyles.Container}>
              <NotesTextarea
                value={model.executionMethod.get()}
                onChange={model.handleExecutionMethodChange}
                placeholder="Add your comments here"
                disabled={disabled}
                readOnly={!isEditing}
                className={PromptInputStyles.Input}
              />
            </InputContainer>
          )}
        </CommentInputWrapper>
      </Vertical>
    </Vertical>
  )
})

export const ServiceListMessage = observer((props: {
  taskId: string
  content: AiChatMessageContent.InitRuntimeConfig.ServiceList
  isActive: boolean
  isAgentRunning: IObservableValue<boolean>
}) => {
  const context = useOrgContext()

  const args: ServiceListModelParams = {
    content: props.content,
    context: context,
    taskId: props.taskId,
    isAgentRunning: props.isAgentRunning
  }
  const viewModel = useModel(() => new ServiceListModel(args), [args.context, args.content, args.taskId])
  const isInEditMode = viewModel.isInEditMode.get()

  return (
    <AgentMessageContainer isActive={props.isActive}>
      <Vertical gap="16px">
        <ServiceList>
          {viewModel.services.map((it, index) => (
            <ServiceItem key={index} model={it} />
          ))}
        </ServiceList>

        <Horizontal gap="8px" alignSelf="flex-end">
          {viewModel.isManuallyEnteredEditMode.get() && (
            <Button kind="alternative" onClick={() => viewModel.reset()} disabled={viewModel.isSubmitting.get()}>
              Cancel
            </Button>
          )}

          {isInEditMode && (
            <Button kind="primary" onClick={viewModel.handleContinue} disabled={!viewModel.canSubmit.get()}>
              {viewModel.submitButtonText.get()}
            </Button>
          )}

          {(!isInEditMode && viewModel.canEnterEditMode.get()) && (
            <Button
              kind="alternative"
              onClick={() => viewModel.setEditMode(true)}
              disabled={viewModel.processing.get()}
            >
              Edit services
            </Button>
          )}
        </Horizontal>
      </Vertical>
    </AgentMessageContainer>
  )
})

const ServiceList = styled(Vertical)`
  gap: 16px;
`

const Disableable = css`
`

const Disabled = css`
  .${Disableable} {
    opacity: 0.4;

    * {
      color: var(--text-color) !important;
    }
  }
`

const ServiceName = styled.div`
  font-size: 18px;
  line-height: 24px;

  font-weight: 500;
`

const ServiceDetails = styled.div`
  ${textStyles.smallFontSize};

  font-weight: 400;
`

const InputContainer = styled.div`
  margin-top: 8px;
`

const CommentLabel = styled(Horizontal)<{ disabled?: boolean }>`
  ${textStyles.smallerFontSize};

  align-items: center;
  gap: 4px;

  width: fit-content;

  color: var(--main-color);

  cursor: ${props => props.disabled ? "auto" : "pointer"};

  font-weight: 400;
`

const CommentInputWrapper = styled.div`
  width: 100%;
`

const NotesTextarea = styled(ResizeableTextarea)`
  ${textStyles.smallFontSize};
  ${textStyles.breakWord};

  width: 100%;
  min-height: 36px;
  padding: 12px 16px;

  resize: vertical;

  border: none;
`